import React from 'react';
import { Description, Group, Title, TitleSection } from './styles';
import { BUTTON_TYPE, EMPTY_STATE_SIZE } from '../../global/constants';
import Button from '../button';
import { isEmpty } from '../../helpers/common';
import { observer } from 'mobx-react-lite';
import Image, { ImageKey } from '../image';

/**
 * Props for the EmptyStateV2 component.
 * @interface Props
 * @property {number} size - Size of the empty state (uses EMPTY_STATE_SIZE constants).
 * @property {string} [buttonTitle] - Optional text for the call-to-action button.
 * @property {() => void} [onClick] - Optional callback when the button is clicked.
 * @property {string} [imageUrl] - Optional image URL to display in the empty state.
 * @property {string} [imageName] - Optional alt text for the image.
 * @property {string} [title] - Optional title to display above the description.
 * @property {string} [description] - Optional description for the empty state.
 * @property {boolean} [isAlignedCenter] - Optional boolean to align the empty state in the center of the page using position absolute.
 * @property {string} [className] - Optional className for the empty state.
 */
interface Props {
  size: number;
  buttonTitle?: string;
  onClick?: () => void;
  imageUrl?: ImageKey;
  imageName?: string;
  title?: string;
  description?: string;
  showButtonIcon?: boolean;
  isAlignedCenter?: boolean;
  className?: string;
}

const EmptyState: React.FC<Props> = ({
  size = EMPTY_STATE_SIZE.MEDIUM,
  buttonTitle,
  onClick,
  imageUrl,
  imageName,
  title,
  description,
  showButtonIcon = true,
  isAlignedCenter = false,
  className
}) => {
  const showTitleSection = !isEmpty(title) || !isEmpty(description);

  return (
    <Group size={size} isAlignedCenter={isAlignedCenter} className={className}>
      {!isEmpty(imageUrl) && (
        <Image
          imageKey={imageUrl}
          alt={imageName || 'empty state'}
          width={size === EMPTY_STATE_SIZE.SMALL ? '160px' : '200px'}
          height={size === EMPTY_STATE_SIZE.SMALL ? '88px' : '110px'}
        />
      )}
      {/* detail section */}
      {showTitleSection && (
        <TitleSection size={size}>
          {!isEmpty(title) && <Title size={size}>{title}</Title>}
          {!isEmpty(description) && <Description>{description}</Description>}
        </TitleSection>
      )}
      {!isEmpty(buttonTitle) && (
        <Button
          title={buttonTitle}
          variant={BUTTON_TYPE.PRIMARY}
          size={size === EMPTY_STATE_SIZE.SMALL ? 'small' : 'medium'}
          onClick={onClick}
          iconName={showButtonIcon ? 'plus-icon' : ''}
          iconPosition={showButtonIcon ? 'start' : 'none'}
        />
      )}
    </Group>
  );
};

export default observer(EmptyState);
